import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { BehaviorSubject, ReplaySubject } from "rxjs";


@Injectable({
  providedIn: "root",
})
export class FetchClassificatorsService {
  locationCode = new ReplaySubject<number>();

  constructor(private http: HttpClient) {
  }

  url: string = environment.backend.baseUrl + environment.backend.contextPath + "/rest/fetch-all-classificators/";

  systemCodes: string[];

  // sessionStorage key/values
  hasClassificators = new BehaviorSubject<boolean>(false);
  observationTypes: BehaviorSubject<ObservationTypes> = new BehaviorSubject<ObservationTypes>(null);
  unitValues: BehaviorSubject<UnitValues> = new BehaviorSubject<UnitValues>(null);
  conditions: BehaviorSubject<Conditions> = new BehaviorSubject<Conditions>(null);
  feelings: BehaviorSubject<Feelings> = new BehaviorSubject<Feelings>(null);
  valueTypes: BehaviorSubject<ValueTypes> = new BehaviorSubject<ValueTypes>(null);
  motivationTypes: BehaviorSubject<MotivationQuestionnaireTypes> = new BehaviorSubject<MotivationQuestionnaireTypes>(null);
  motivationUnits: BehaviorSubject<MotivationQuestionnaireUnits> = new BehaviorSubject<MotivationQuestionnaireUnits>(null);
  statusTypes: BehaviorSubject<StatusTypes> = new BehaviorSubject<StatusTypes>(null);
  diagnosesTypes: BehaviorSubject<DiagnosesTypes> = new BehaviorSubject<DiagnosesTypes>(null);
  goalTypes: BehaviorSubject<GoalTypes> = new BehaviorSubject<GoalTypes>(null);
  activityTypes: BehaviorSubject<ActivityTypes> = new BehaviorSubject<ActivityTypes>(null);
  repeatPeriods: BehaviorSubject<PeriodRepeats> = new BehaviorSubject<PeriodRepeats>(null);
  taskTypes: BehaviorSubject<TaskTypes> = new BehaviorSubject<TaskTypes>(null);
  relativeTypes: BehaviorSubject<RelativeTypes> = new BehaviorSubject<RelativeTypes>(null);
  paidServiceTypes: BehaviorSubject<PaidServiceTypes> = new BehaviorSubject<PaidServiceTypes>(null);
  paymentStatuses: BehaviorSubject<PaymentStatuses> = new BehaviorSubject<PaymentStatuses>(null);
  messageStatuses: BehaviorSubject<MessageStatuses> = new BehaviorSubject<MessageStatuses>(null);
  prescriptionStatuses: BehaviorSubject<PrescriptionStatuses> = new BehaviorSubject<PrescriptionStatuses>(null);

  fetchAllTypeIds() {
    this.systemCodes = [
      "MESSAGE_TYPE",
      "CONTACT_TYPE",
      "REASON",
      "ELEMENT_TYPE",
      "MESSAGE_DIRECTION",
      "MESSAGE_STATUS",
      "AKT",
      "DISEASE",
      "AKT_Q1_ANSWER_SCALE",
      "EEK2",
      "MEETING_STATUS",
      "MEASUREMENT_TYPE",
      "CONDITION",
      "FEELING",
      "VALUE_TYPE",
      "HP_STATUS",
      "HP_DIAGNOSES",
      "HP_GOAL_TYPE",
      "HP_ACTIVITY_TYPE",
      "REPEAT_PERIOD",
      "HP_TASK_TYPE",
      "RELATIVE_TYPE",
      "UNIT_OF_MEASURE",
      "FAMILY_ANAMNESIS",
      "AUDIT",
      "MOTIVATION_TEST",
      "QTLY_FEEDBACK",
      "ACTIVE_MINUTES",
      "REQUIRE_TEST",
      "GNRL_FEEDBACK",
      "EXTERNAL_LOGIN_TYPE",
      "PAID_SERVICE",
      "PAYMENT_STATUS",
      "MESSAGE_STATUS",
      "PRESCRIPTION_STATUS"
    ];
    return this.http
      .post<Classificator[]>(this.url, this.systemCodes, {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        if (response.body != null) {
          response.body.forEach((classificator) => {
            classificator.classificatorCodes.forEach((code) => {
              sessionStorage.setItem(classificator.systemCode + "_" + code.code, JSON.stringify(code.id));
            });
          });
          this.assignConditions();
          this.assignUnitValues();
          this.assignValueTypes();
          this.assignFeelings();
          this.assignObservationTypes();
          this.motivationQuestionnaireTypes();
          this.assignHealthPlanStatus();
          this.assignHealthPlanDiagnoses();
          this.assignHealthPlanGoalType();
          this.assignHealthPlanActivityType();
          this.assignHealthPlanPeriodRepeat();
          this.assignHealthPlanTaskTypes();
          this.assignHealthPlanRelativeTypes();
          this.assignPaidServiceTypes();
          this.assignPaymentStatuses();
          this.assignMessageStatuses();
          this.assignPrescriptionStatuses();
          this.hasClassificators.next(true);
          return true;
        } else {
          return false;
        }
      });
  }

  assignConditions() {
    let conditions = {
      resting: Number(sessionStorage.getItem("CONDITION_1")),
      underPressure: Number(sessionStorage.getItem("CONDITION_2")),
      beforeMedication: Number(sessionStorage.getItem("CONDITION_3")),
      afterMedication: Number(sessionStorage.getItem("CONDITION_4")),
      fasting: Number(sessionStorage.getItem("CONDITION_5")),
      beforeEating: Number(sessionStorage.getItem("CONDITION_6")),
      afterEating: Number(sessionStorage.getItem("CONDITION_7")),
      sitting: Number(sessionStorage.getItem("CONDITION_8")),
      standingUp: Number(sessionStorage.getItem("CONDITION_9")),
      unknown: Number(sessionStorage.getItem("CONDITION_99")),
    };
    this.conditions.next(conditions);
  }

  assignUnitValues() {
    let unitValues = {
      kg: Number(sessionStorage.getItem("UNIT_OF_MEASURE_1")),
      cm: Number(sessionStorage.getItem("UNIT_OF_MEASURE_2")),
      kg_m2: Number(sessionStorage.getItem("UNIT_OF_MEASURE_3")),
      mmHg: Number(sessionStorage.getItem("UNIT_OF_MEASURE_4")),
      bpm_min: Number(sessionStorage.getItem("UNIT_OF_MEASURE_5")),
      mmol_l: Number(sessionStorage.getItem("UNIT_OF_MEASURE_6")),
      mg_dl: Number(sessionStorage.getItem("UNIT_OF_MEASURE_7")),
      g_dl: Number(sessionStorage.getItem("UNIT_OF_MEASURE_8")),
      grad_C0: Number(sessionStorage.getItem("UNIT_OF_MEASURE_9")),
      percent: Number(sessionStorage.getItem("UNIT_OF_MEASURE_10")),
      g: Number(sessionStorage.getItem("UNIT_OF_MEASURE_11")),
      m: Number(sessionStorage.getItem("UNIT_OF_MEASURE_12")),
      l_min: Number(sessionStorage.getItem("UNIT_OF_MEASURE_13")),
    };
    this.unitValues.next(unitValues);
  }

  assignValueTypes() {
    let valueTypes = {
      NUMBER: Number(sessionStorage.getItem("VALUE_TYPE_1")),
      DATETIME: Number(sessionStorage.getItem("VALUE_TYPE_2")),
      BOOLEAN: Number(sessionStorage.getItem("VALUE_TYPE_3")),
      TEXT: Number(sessionStorage.getItem("VALUE_TYPE_4")),
    };
    this.valueTypes.next(valueTypes);
  }

  assignFeelings() {
    let feelings = {
      good: Number(sessionStorage.getItem("FEELING_1")),
      neutral: Number(sessionStorage.getItem("FEELING_2")),
      bad: Number(sessionStorage.getItem("FEELING_3")),
    };
    this.feelings.next(feelings);
  }

  assignObservationTypes() {
    let observationTypes = {
      weight: Number(sessionStorage.getItem("MEASUREMENT_TYPE_1")),
      height: Number(sessionStorage.getItem("MEASUREMENT_TYPE_2")),
      waist: Number(sessionStorage.getItem("MEASUREMENT_TYPE_3")),
      bloodPressure: Number(sessionStorage.getItem("MEASUREMENT_TYPE_4")),
      systolic: Number(sessionStorage.getItem("MEASUREMENT_TYPE_4.1")),
      diastolic: Number(sessionStorage.getItem("MEASUREMENT_TYPE_4.2")),
      pulse: Number(sessionStorage.getItem("MEASUREMENT_TYPE_5")),
      bloodSugar: Number(sessionStorage.getItem("MEASUREMENT_TYPE_6")),
      cholesterol: Number(sessionStorage.getItem("MEASUREMENT_TYPE_7")),
      LDL_cholesterol: Number(sessionStorage.getItem("MEASUREMENT_TYPE_7.1")),
      HDL_cholesterol: Number(sessionStorage.getItem("MEASUREMENT_TYPE_7.2")),
      triglyceride: Number(sessionStorage.getItem("MEASUREMENT_TYPE_7.3")),
      hemoglobin: Number(sessionStorage.getItem("MEASUREMENT_TYPE_8")),
      bodyTemp: Number(sessionStorage.getItem("MEASUREMENT_TYPE_9")),
      oxygenSaturation: Number(sessionStorage.getItem("MEASUREMENT_TYPE_10")),
      BMI: Number(sessionStorage.getItem("MEASUREMENT_TYPE_11")),
      headMeasurement: Number(sessionStorage.getItem("MEASUREMENT_TYPE_12")),
      PEF: Number(sessionStorage.getItem("MEASUREMENT_TYPE_13")),
      movementActivity: Number(sessionStorage.getItem("MEASUREMENT_TYPE_14")),
      movement_LIGHTLY: Number(sessionStorage.getItem("ACTIVE_MINUTES_LIGHTLY")),
      movement_MODERATELY: Number(sessionStorage.getItem("ACTIVE_MINUTES_MODERATELY")),
      movement_VERY: Number(sessionStorage.getItem("ACTIVE_MINUTES_VERY")),
      note: Number(sessionStorage.getItem("MEASUREMENT_TYPE_15")),

    };
    this.observationTypes.next(observationTypes);
  }

  motivationQuestionnaireTypes() {
    let motTypes = {
      yesNoAnswer: String(sessionStorage.getItem("QUESTION_ANSWER_YES_NO_QUESTION_ANSWER_YES_NO")),
      number: Number(sessionStorage.getItem("VALUE_TYPE_1")),
      text: String(sessionStorage.getItem("VALUE_TYPE_4")),
    };
    this.motivationTypes.next(motTypes);
    let motUnits = {
      kg: Number(sessionStorage.getItem("UNIT_OF_MEASURE_1")), cm: Number(sessionStorage.getItem("UNIT_OF_MEASURE_2")),
    };
    this.motivationUnits.next(motUnits);
  }

  assignHealthPlanStatus() {
    let statuses = {
      DRAFT: Number(sessionStorage.getItem("HP_STATUS_DRAFT")),
      ACTIVE: Number(sessionStorage.getItem("HP_STATUS_ACTIVE")),
      ENTERED_ERROR: Number(sessionStorage.getItem("HP_STATUS_ENTERED_IN_ERROR")),
      COMPLETED: Number(sessionStorage.getItem("HP_STATUS_COMPLETED")),
      OBSOLETE: Number(sessionStorage.getItem("HP_STATUS_OBSOLETE")),
    };
    this.statusTypes.next(statuses);
  }

  assignHealthPlanDiagnoses() {
    let diagnoses = {
      ASTHMA: Number(sessionStorage.getItem("HP_DIAGNOSES_1")),
      DIABETES: Number(sessionStorage.getItem("HP_DIAGNOSES_2")),
      HIGH_BLOOD_PRESSURE: Number(sessionStorage.getItem("HP_DIAGNOSES_3")),
      ALCOHOL_USE_DISORDER: Number(sessionStorage.getItem("HP_DIAGNOSES_4")),
      HYPERLIPIDEMIA: Number(sessionStorage.getItem("HP_DIAGNOSES_5")),
      COPD: Number(sessionStorage.getItem("HP_DIAGNOSES_6")),
      HEART_FAILURE: Number(sessionStorage.getItem("HP_DIAGNOSES_7")),
      ATRIAL_FIBRILLATION: Number(sessionStorage.getItem("HP_DIAGNOSES_8")),
      CEREBRAL_INFARCTION: Number(sessionStorage.getItem("HP_DIAGNOSES_9")),
      OBESITY: Number(sessionStorage.getItem("HP_DIAGNOSES_10")),
      BULIMIA_NERVOSA: Number(sessionStorage.getItem("HP_DIAGNOSES_11")),
      DEPRESSION: Number(sessionStorage.getItem("HP_DIAGNOSES_12")),
      ANOREXIA_NERVOSA: Number(sessionStorage.getItem("HP_DIAGNOSES_13")),
      TOBACCO_USE: Number(sessionStorage.getItem("HP_DIAGNOSES_14")),
      GOUT: Number(sessionStorage.getItem("HP_DIAGNOSES_15")),
      PSORIASIS: Number(sessionStorage.getItem("HP_DIAGNOSES_16")),
      THYROID_DISORDER: Number(sessionStorage.getItem("HP_DIAGNOSES_17")),
      HEADACHE: Number(sessionStorage.getItem("HP_DIAGNOSES_18")),
      ALL: Number(sessionStorage.getItem("HP_DIAGNOSES_ALL")),
    };
    this.diagnosesTypes.next(diagnoses);
  }

  assignHealthPlanGoalType() {
    let goalType = {
      BLOOD_PRESSURE: Number(sessionStorage.getItem("HP_GOAL_TYPE_BLOOD_PRESSURE")),
      WEIGHT: Number(sessionStorage.getItem("HP_GOAL_TYPE_WEIGHT")),
      MOTIVATIONAL: Number(sessionStorage.getItem("HP_GOAL_TYPE_MOTIVATIONAL")),
      PEF: Number(sessionStorage.getItem("HP_GOAL_TYPE_PEF")),
      BLOOD_SUGAR: Number(sessionStorage.getItem("HP_GOAL_TYPE_BLOOD_SUGAR")),
    };
    this.goalTypes.next(goalType);
  }

  assignHealthPlanActivityType() {
    let activityType = {
      MOOD: Number(sessionStorage.getItem("HP_ACTIVITY_TYPE_MOOD")),
      NUTRITION: Number(sessionStorage.getItem("HP_ACTIVITY_TYPE_NUTRITION")),
      PHYSICAL_HIGH: Number(sessionStorage.getItem("HP_ACTIVITY_TYPE_PHYSICAL_HIGH")),
      SLEEP: Number(sessionStorage.getItem("HP_ACTIVITY_TYPE_SLEEP")),
      MEDICATION: Number(sessionStorage.getItem("HP_ACTIVITY_TYPE_MEDICATION")),
      MEASUREMENT: Number(sessionStorage.getItem("HP_ACTIVITY_TYPE_MEASUREMENT")),
      PHYSICAL_LOW: Number(sessionStorage.getItem("HP_ACTIVITY_TYPE_PHYSICAL_LOW")),
      HABIT: Number(sessionStorage.getItem("HP_ACTIVITY_TYPE_HABIT")),
    };
    this.activityTypes.next(activityType);
  }

  assignHealthPlanPeriodRepeat() {
    let periodRepeat = {
      NO_REPEAT: Number(sessionStorage.getItem("REPEAT_PERIOD_NO_REPEAT")),
      DAY: Number(sessionStorage.getItem("REPEAT_PERIOD_DAY")),
      CONTINUOUS: Number(sessionStorage.getItem("REPEAT_PERIOD_CONTINUOUS")),
      WEEK: Number(sessionStorage.getItem("REPEAT_PERIOD_WEEK")),
    };
    this.repeatPeriods.next(periodRepeat);
  }

  assignHealthPlanTaskTypes() {
    let taskTypes = {
      VISIT: Number(sessionStorage.getItem("HP_TASK_TYPE_VISIT")),
      MISSING: Number(sessionStorage.getItem("HP_TASK_TYPE_MISSING")),
      OTHER: Number(sessionStorage.getItem("HP_TASK_TYPE_OTHER")),
      ABNORMAL: Number(sessionStorage.getItem("HP_TASK_TYPE_ABNORMAL")),
      VIDEO: Number(sessionStorage.getItem("HP_TASK_TYPE_VIDEO")),
      PRAISE: Number(sessionStorage.getItem("HP_TASK_TYPE_PRAISE")),
      SPECIALITY: Number(sessionStorage.getItem("HP_TASK_TYPE_SPECIALITY")),
      HEALTH_PLAN: 0,
      HEALTH_PLAN_START: 1,
    };
    this.taskTypes.next(taskTypes);
  }

  assignHealthPlanRelativeTypes() {
    let relativeTypes = {
      SPOUSE: Number(sessionStorage.getItem("RELATIVE_TYPE_SPOUSE")),
      OTHER: Number(sessionStorage.getItem("RELATIVE_TYPE_OTHER")),
      MOTHER: Number(sessionStorage.getItem("RELATIVE_TYPE_MOTHER")),
      FATHER: Number(sessionStorage.getItem("RELATIVE_TYPE_FATHER")),
      CHILD: Number(sessionStorage.getItem("RELATIVE_TYPE_CHILD")),
    };
    this.relativeTypes.next(relativeTypes);
  }

  assignPaidServiceTypes() {
    let paidServiceTypes = {
      PAID_PRESCRIPTION: Number(sessionStorage.getItem("PAID_SERVICE_PAID_PRESCRIPTION")),
      PAID_CONSULTATION: Number(sessionStorage.getItem("PAID_SERVICE_PAID_CONSULTATION")),
      PAID_SICK_LEAVE: Number(sessionStorage.getItem("PAID_SERVICE_PAID_SICK_LEAVE")),
    };
    this.paidServiceTypes.next(paidServiceTypes);
  }

  assignPaymentStatuses() {
    let paymentStatuses = {
      CREATED: Number(sessionStorage.getItem("PAYMENT_STATUS_CREATED")),
      PENDING: Number(sessionStorage.getItem("PAYMENT_STATUS_PENDING")),
      PAID: Number(sessionStorage.getItem("PAYMENT_STATUS_PAID")),
      ABANDONED: Number(sessionStorage.getItem("PAYMENT_STATUS_ABANDONED")),
      VOIDED: Number(sessionStorage.getItem("PAYMENT_STATUS_VOIDED")),
    };
    this.paymentStatuses.next(paymentStatuses);
  }

  assignMessageStatuses() {
    let messageStatuses = {
      NEW: Number(sessionStorage.getItem("MESSAGE_STATUS_NEW")),
      IN_PROGRESS: Number(sessionStorage.getItem("MESSAGE_STATUS_IN_PROGRESS")),
      COMPLETED: Number(sessionStorage.getItem("MESSAGE_STATUS_COMPLETED")),
    };
    this.messageStatuses.next(messageStatuses);
  }

  assignPrescriptionStatuses(){
    let prescriptionStatuses = {
      ORDERED: Number(sessionStorage.getItem("PRX_ORDERED")),
      ISSUED: Number(sessionStorage.getItem("PRX_ISSUED")),
      ISSUED_HC: Number(sessionStorage.getItem("PRX_ISSUED_HC")),
    };
    this.prescriptionStatuses.next(prescriptionStatuses);
  }
}


export interface StatusTypes {
  DRAFT: number;
  ACTIVE: number;
  ENTERED_ERROR: number;
  COMPLETED: number;
  OBSOLETE: number;
}

export interface DiagnosesTypes {
  ASTHMA: number;
  DIABETES: number;
  HIGH_BLOOD_PRESSURE: number;
  ALCOHOL_USE_DISORDER: number;
  HYPERLIPIDEMIA: number;
  COPD: number;
  HEART_FAILURE: number;
  ATRIAL_FIBRILLATION: number;
  CEREBRAL_INFARCTION: number;
  OBESITY: number;
  BULIMIA_NERVOSA: number;
  DEPRESSION: number;
  ANOREXIA_NERVOSA: number;
  TOBACCO_USE: number;
  GOUT: number;
  PSORIASIS: number;
  THYROID_DISORDER: number;
  HEADACHE: number;
  ALL: number;
}

export interface GoalTypes {
  BLOOD_PRESSURE: number;
  WEIGHT: number;
  MOTIVATIONAL: number;
  PEF: number;
  BLOOD_SUGAR: number;
}

export interface ActivityTypes {
  MOOD: number;
  NUTRITION: number;
  PHYSICAL_HIGH: number;
  SLEEP: number;
  MEDICATION: number;
  MEASUREMENT: number;
  PHYSICAL_LOW: number;
  HABIT: number;
}

export interface PeriodRepeats {
  NO_REPEAT: number;
  DAY: number;
  CONTINUOUS: number;
  WEEK: number;
}

export interface TaskTypes {
  VISIT: number;
  MISSING: number;
  OTHER: number;
  ABNORMAL: number;
  VIDEO: number;
  PRAISE: number;
  SPECIALITY: number;
  HEALTH_PLAN: number;
  HEALTH_PLAN_START: number;
}

export interface RelativeTypes {
  SPOUSE: number;
  OTHER: number;
  MOTHER: number;
  FATHER: number;
  CHILD: number;
}

export interface PaidServiceTypes {
  PAID_PRESCRIPTION: number;
  PAID_CONSULTATION: number;
  PAID_SICK_LEAVE: number;
}

export interface PaymentStatuses {
  CREATED: number;
  PENDING: number;
  PAID: number;
  ABANDONED: number;
  VOIDED: number;
}

export interface MessageStatuses {
  NEW: number;
  IN_PROGRESS: number;
  COMPLETED: number;
}

export interface PrescriptionStatuses {
  ORDERED: number;
  ISSUED: number;
  ISSUED_HC: number;
}

export interface Classificator {
  systemCode: string;
  classificatorCodes: ClassificatorCode[];
}

export interface ClassificatorCode {
  classificatorSystemId: number;
  code: string;
  id: number;
  insertTime: string;
  name: string;
  type: string;
}

export class ObservationTypes {
  waist: number;
  bloodPressure: number;
  triglyceride: number;
  bloodSugar: number;
  oxygenSaturation: number;
  BMI: number;
  weight: number;
  bodyTemp: number;
  hemoglobin: number;
  PEF: number;
  systolic: number;
  diastolic: number;
  LDL_cholesterol: number;
  headMeasurement: number;
  HDL_cholesterol: number;
  pulse: number;
  cholesterol: number;
  movementActivity: number;
  height: number;
  movement_LIGHTLY: number;
  movement_MODERATELY: number;
  movement_VERY: number;
  note: number;
}

export interface MotivationQuestionnaireTypes {
  number: number;
  yesNoAnswer: string;
  text: string;
}

export interface MotivationQuestionnaireUnits {
  cm: number;
  kg: number;
}

export interface Conditions {
  afterEating: number;
  resting: number;
  underPressure: number;
  fasting: number;
  beforeEating: number;
  beforeMedication: number;
  afterMedication: number;
  sitting: number;
  standingUp: number;
  unknown: number;
}

export interface UnitValues {
  l_min: number;
  g: number;
  cm: number;
  mmHg: number;
  percent: number;
  m: number;
  grad_C0: number;
  g_dl: number;
  kg: number;
  kg_m2: number;
  bpm_min: number;
  mg_dl: number;
  mmol_l: number;
}

export interface Feelings {
  bad: number;
  neutral: number;
  good: number;
}

export interface ValueTypes {
  NUMBER: number;
  DATETIME: number;
  TEXT: number;
  BOOLEAN: number;
}
